import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { intelligenceApi, type SitePageView } from '../api/intelligence'
import { formatDateBR, formatDateTimeBR } from '../lib/format'
import { Spinner } from './Spinner'

const money = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
const display = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '')

/**
 * SITE E PÁGINAS DE DESTINO — o que a IA leu do site do cliente (perfil do negócio, relido
 * todo mês) e as páginas que recebem o dinheiro do Google, checadas todo dia: no ar ou não,
 * o que oferecem e quando mudaram. Tudo isso entra na análise semanal.
 */
export function ClientSite({ clientId }: { clientId: string }) {
  const qc = useQueryClient()
  const queryKey = ['client-site', clientId]
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => intelligenceApi.site(clientId) })
  const refresh = useMutation({
    mutationFn: () => intelligenceApi.refreshSite(clientId),
    onSuccess: (res) => {
      qc.setQueryData(queryKey, res)
      qc.invalidateQueries({ queryKey: ['client-insights', clientId] })
      qc.invalidateQueries({ queryKey: ['intelligence-inbox'] })
    },
  })

  if (isLoading) return <Spinner />
  const pages = data?.pages ?? []
  const problems = pages.filter((p) => p.problem)

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        A IA não navega na internet: ela lê o que buscamos aqui. As páginas que mais recebem cliques do Google são
        checadas <strong>todo dia</strong> (no ar? o que oferecem? mudou?) e o site é relido todo mês. O que o site diz
        do negócio vira <strong>proposta</strong> no dossiê, para você confirmar.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          title="Busca as páginas de destino dos últimos 30 dias, checa cada uma e relê o site inteiro."
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {refresh.isPending && <Spinner className="h-3.5 w-3.5" />}
          {refresh.isPending ? 'Lendo o site e as páginas (até 2 min)…' : 'Ler o site agora'}
        </button>
        {refresh.isSuccess && refresh.data.result && (
          <span className="text-xs text-emerald-700">
            {refresh.data.result.ran
              ? `Lido. ${refresh.data.result.facts} fato(s) proposto(s) ao dossiê.`
              : refresh.data.result.reason}
          </span>
        )}
        {refresh.isError && <span className="text-xs text-red-600">Falhou: {String((refresh.error as Error).message)}</span>}
      </div>

      {problems.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <strong>⚠️ {problems.length} página(s) com problema</strong> — os anúncios estão mandando gente para elas:
          <ul className="mt-1 list-disc pl-5 text-xs">
            {problems.map((p) => (
              <li key={p.id}>
                {display(p.url)}: {p.problem}
                {p.problemSince ? ` (desde ${formatDateBR(p.problemSince)})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
        <div className="mb-1 text-xs font-semibold text-slate-600">
          O que o site diz do negócio
          {data?.siteHome && (
            <a href={data.siteHome} target="_blank" rel="noreferrer" className="ml-1 font-normal text-brand-700 hover:underline">
              ({display(data.siteHome)})
            </a>
          )}
          {data?.siteProfileAt && <span className="ml-1 font-normal text-slate-400">· lido em {formatDateBR(data.siteProfileAt)}</span>}
        </div>
        <p className="text-sm text-slate-700">
          {data?.siteProfile ?? 'Ainda não lido — acontece na próxima rodada diária ou pelo botão acima.'}
        </p>
      </div>

      {pages.length === 0 ? (
        <p className="text-sm text-slate-500">
          Nenhuma página monitorada ainda (vêm das páginas de destino do Google dos últimos 30 dias).
        </p>
      ) : (
        <ul className="space-y-2">
          {pages.map((p) => (
            <PageItem key={p.id} page={p} />
          ))}
        </ul>
      )}
    </div>
  )
}

function PageItem({ page: p }: { page: SitePageView }) {
  const cvr = p.clicks30d > 0 ? (p.conv30d / p.clicks30d) * 100 : null
  const s = p.signals
  const yes = (v: boolean | null | undefined) => (v == null ? '?' : v ? 'sim' : 'não')
  const recentChange = p.changedAt && Date.now() - new Date(p.changedAt).getTime() < 30 * 86400_000
  return (
    <li className={`rounded-lg border bg-white p-3 ${p.problem ? 'border-red-300' : 'border-slate-200'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <a href={p.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-brand-700 hover:underline">
          {display(p.url)}
        </a>
        {p.problem ? (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">Com problema</span>
        ) : p.checkedAt ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">No ar</span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">Ainda não checada</span>
        )}
        {p.via === 'reader' && (
          <span className="text-[11px] text-slate-400" title="Página montada por JavaScript: lida por um leitor externo.">
            lida por leitor
          </span>
        )}
        {p.via === 'blocked' && <span className="text-[11px] text-amber-600">o site bloqueia leitura automática</span>}
      </div>
      <p className="mt-0.5 text-xs text-slate-500">
        30 dias: {money(p.spend30d)} · {Math.round(p.clicks30d)} cliques · {p.conv30d.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} conv.
        {cvr != null ? ` · ${cvr.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% de conversão` : ''}
        {p.checkedAt ? ` · checada ${formatDateTimeBR(p.checkedAt)}` : ''}
      </p>
      {p.problem && (
        <p className="mt-1 text-sm text-red-700">
          {p.problem}
          {p.problemSince ? ` — desde ${formatDateBR(p.problemSince)}` : ''}
          {p.okAt ? ` (última vez no ar: ${formatDateBR(p.okAt)})` : ''}
        </p>
      )}
      {p.summary && <p className="mt-1 text-sm text-slate-700">{p.summary}</p>}
      {s && (
        <p className="mt-1 text-xs text-slate-500">
          WhatsApp {yes(s.whatsapp)} · formulário {yes(s.formulario)} · telefone {yes(s.telefone)}
          {s.precos?.length ? ` · preços: ${s.precos.join(', ')}` : ''}
        </p>
      )}
      {recentChange && p.changeSummary && (
        <p className="mt-1 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">
          Mudou em {formatDateBR(p.changedAt)}: {p.changeSummary}
        </p>
      )}
    </li>
  )
}
